import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getDomains } from '@/api/tracker-domains';
import { Select } from './Select';

interface Props {
  onPick: (url: string) => void;
  className?: string;
  placeholder?: string;
}

/**
 * Small dropdown that lets the user pick a tracker campaign and inject its
 * full URL into a sibling text input. Pairs with manual URL entry.
 */
export function TrackerLinkPicker({ onPick, className = '', placeholder = 'Pick from tracker' }: Props) {
  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: domains = [] } = useQuery({ queryKey: ['tracker-domains'], queryFn: getDomains });

  const options = useMemo(() => {
    const hostById = new Map(domains.map((d) => [d.id, d.hostname]));
    return campaigns
      .filter((c) => c.status !== 'archived' && c.status !== 'deleted')
      .map((c) => {
        const host = hostById.get(c.domain_id);
        if (!host) return null;
        const url = `https://${host}/${c.slug}`;
        const paused = c.status === 'paused';
        return {
          value: url,
          label: paused ? `${c.name} (paused) — ${host}/${c.slug}` : `${c.name} — ${host}/${c.slug}`,
        };
      })
      .filter((o): o is { value: string; label: string } => !!o);
  }, [campaigns, domains]);

  if (options.length === 0) return null;

  return (
    <Select
      value=""
      options={options}
      onChange={(v) => { if (v) onPick(v); }}
      placeholder={placeholder}
      searchable
      size="sm"
      className={className}
    />
  );
}
